/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useLocation, Link } from 'react-router-dom';
import {
  useState, useEffect, useMemo, useLayoutEffect,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { ContactBox } from '@purple.com/react-components';
import rightCaret from '../../../../assets/svgs/separator.svg';
import OrderTracker from '../../../shared/blocks/order-tracker/order-tracker';
import GlobalError from '../../../shared/blocks/global-error/global-error';
import './view-order.scss';
import useIsDesktop from '../../../../utilities/hooks/useIsDesktop';
import { convertCentsToDollars, calculatetShippingTotal, convertMonthsToYears } from '../../../../utilities/utils';
import useOrdersHistory from '../../../../utilities/hooks/useOrdersHistory';
import useOrder from '../../../../utilities/hooks/useOrder';
import Loader from '../../../shared/blocks/loader/loader';
import ShipmentInfo from './shipment-info';
import SummaryBox from './summary-box';
import OrderSummary from './order-summary';
import WarrantyInfo from './warranty-info';
import WarrantyItems from './warranty-items';
import {
  WARRANTY_LINE_ITEM,
  WARRANTY_IMAGE_URL,
  ORDER_STATUS_IN_PROGRESS,
  ORDER_STATUS_SHIPPED,
  ORDER_STATUS_DELIVERED,
  ORDER_STATUS_COMPLETE,
  ORDER_STATUS_PENDING,
  ORDER_STATUS_CANCELED,
  MILLISECONDS_DAY_CALCULATION,
  NUMBER_THIRTY,
} from './constants';

const ViewOrder = () => {
  const isDesktop = useIsDesktop();
  const [shipments, setShipments] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const orders = useSelector((state) => state.accounts.user.orders);
  const location = useLocation().search;
  const params = new URLSearchParams(location);
  const orderNumber = params.get('order');
  const [shippingMethods, setShippingMethods] = useState(null);
  const { isLoading: isLoadingOrders } = useOrdersHistory();

  const currentOrderId = useMemo(() => orders.find((o) => {
    if (o.orderNumber === orderNumber) {
      return o.id;
    }
    return false;
  })?.id, [orderNumber, orders]);

  const order = useOrder(currentOrderId);

  const setupShipments = (o) => {
    const inProgressShipment = [];
    const deliveredShipment = [];
    const shipment = [];
    const products = o.lineItems;
    // TODO: Update this logic if CT ends up sending different deliveries in the future
    // eslint-disable-next-line no-constant-condition
    if (products?.length > 0 && (
      o.shipMentState === ORDER_STATUS_IN_PROGRESS || ORDER_STATUS_PENDING
    )) {
      products?.forEach((product) => inProgressShipment.push(product));
    } else if (products?.length > 0 && o.shipMentState === ORDER_STATUS_DELIVERED) {
      products?.forEach((product) => deliveredShipment.push(product));
    }
    if (inProgressShipment.length > 0) {
      shipment.push(inProgressShipment);
    }
    if (deliveredShipment.length > 0) {
      shipment.push(deliveredShipment);
    }
    return shipment;
  };

  const filterShippingMethodsAndGetNames = (
    customLineItems,
    locale,
    lineItems,
  ) => {
    const itemsShippingMap = lineItems.map((lineItem) =>
      customLineItems.reduce((prevLineItem, currLineItem) => ({
        ...prevLineItem,
        ...(
          (currLineItem?.custom?.fields?.netsuite_shipping_name &&
            currLineItem?.custom?.fields?.lineItemsIds.includes(lineItem.id)) && {
            shippingMethod: {
              name: lineItem.name?.[locale],
              shipping: currLineItem?.name?.[locale],
              method: currLineItem?.slug,
            },
          }),
      }), {})).reduce((acc, obj) => ({
      ...acc,
      [obj.shippingMethod.method]:
          [...(acc[obj.shippingMethod.method] ?? []), obj.shippingMethod],

    }), {});

    return itemsShippingMap;
  };

  const orderDate = useMemo(() => {
    if (order?.createdAt) {
      const createdAt = new Date(order.createdAt);
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      }).format(createdAt);
      return formattedDate;
    }
    return null;
  }, [order.createdAt]);

  useEffect(() => {
    const getWarrantyLineItem = (fields) => {
      const product = order?.lineItems?.find((item) => item?.id === fields.lineItemsIds[0]);
      return product.name['en-US'];
    };
    const checkForWarrantyItems = (customLineItems) => {
      const filteredItems = customLineItems.filter(
        (item) => item?.custom?.fields?.type === WARRANTY_LINE_ITEM);
      const warrantyItems = Object.assign(filteredItems).map((warranty) => ({
        id: warranty.id,
        name: warranty.name,
        totalPrice: warranty.totalPrice,
        quantity: warranty.quantity,
        discountedPricePerQuantity: warranty.discountedPricePerQuantity,
        variant: {
          attributes: {
            product: `Product: ${getWarrantyLineItem(warranty.custom.fields)}`,
            term: `Term: ${convertMonthsToYears(warranty.custom.fields.warrantyTerm)} years`,
          },
          images: [
            {
              url: WARRANTY_IMAGE_URL,
            },
          ],
        },
      }));
      return warrantyItems;
    };
    if (order?.customLineItems) {
      setShipments(setupShipments(order));
      setWarranties(checkForWarrantyItems(order.customLineItems));

      const shipMethodItemMap = filterShippingMethodsAndGetNames(
        order.customLineItems,
        'en-US',
        order.lineItems,
      );
      setShippingMethods(shipMethodItemMap);
    }
  }, [order]);

  const printPage = () => {
    window.print();
  };

  useLayoutEffect(() => {
    // fix safari initial scroll position
    setTimeout(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 100);
  }, []);

  const orderStatus = (status) => {
    let newOrderStatus = '';
    if (status === ORDER_STATUS_DELIVERED) {
      newOrderStatus = ORDER_STATUS_COMPLETE;
    } else if (status === ORDER_STATUS_SHIPPED) {
      newOrderStatus = ORDER_STATUS_SHIPPED;
    } else {
      newOrderStatus = ORDER_STATUS_IN_PROGRESS;
    }
    return newOrderStatus;
  };

  const orderCreatedAt = new Date(order.createdAt);
  const calculateWhenOrderIsComplete = (dateOfOrder, statusOfOrder) => {
    if (statusOfOrder !== ORDER_STATUS_DELIVERED) {
      return orderStatus(statusOfOrder);
    }

    const today = new Date();
    const calculatedMilliseconds = today.getTime() - dateOfOrder.getTime();
    const calculatedDays = Math.floor(calculatedMilliseconds / MILLISECONDS_DAY_CALCULATION);

    if (statusOfOrder === ORDER_STATUS_DELIVERED && calculatedDays > NUMBER_THIRTY) {
      return ORDER_STATUS_COMPLETE;
    }

    return statusOfOrder;
  };

  return (
    <div className="view-order-page">
      {!isLoadingOrders && order?.shippingAddress && order?.customLineItems[0] && orderDate ? (
        <>
          <div className="view-order-page-header">
            <Link to="/orders" className="c-purple-primary view-order-page-header-link">Order History</Link>
            <img src={rightCaret} alt="right caret" className="view-order-page-header-caret" />
            <h3 className="view-order-page-header-h3 c-purple-primary fs-16">{`Order #: ${orderNumber}`}</h3>
          </div>
          <GlobalError />
          <div className="view-order-header">
            <div className="d-flex flex-column align-items-start justify-content-between">
              <div>
                <div className="d-flex align-items-center">
                  <h3 className="ff-wallop c-purple-primary account-page-name tt-capitals" data-testid={order.orderNumber}>{`Order #: ${order.orderNumber}`}</h3>
                </div>
              </div>
              {isDesktop && (
                <div className="d-flex m-t-s">
                  <p className="c-purple-primary fs-16">Order Status:</p>
                  <div className={classNames(
                    'view-order-item-status', {
                      'view-order-item-status-in-progress': order.orderState !== ORDER_STATUS_CANCELED && orderStatus(order.shipmentState) === ORDER_STATUS_IN_PROGRESS,
                      'view-order-item-status-shipped': order.orderState !== ORDER_STATUS_CANCELED && orderStatus(order.shipmentState) === ORDER_STATUS_SHIPPED,
                      'view-order-item-status-complete': order.orderState !== ORDER_STATUS_CANCELED && orderStatus(order.shipmentState) === ORDER_STATUS_COMPLETE,
                      'view-order-item-status-cancelled': order.orderState === ORDER_STATUS_CANCELED,
                    },
                  )}
                  >
                    {order.orderState === ORDER_STATUS_CANCELED ?
                      order.orderState :
                      calculateWhenOrderIsComplete(orderCreatedAt, order.shipmentState)}
                  </div>
                </div>
              )}
              {orderDate && (
                <p className="c-purple-primary fs-16 view-order-order-placed">
                  {`Order Placed on ${orderDate}`}
                </p>
              )}
            </div>
          </div>
          {!isDesktop && (
            <div className="shipping-billing-print-tracking">
              <button
                type="button"
                className="print-link button button--default"
                onClick={printPage}
              >
                Print
              </button>
              <OrderTracker orderNum={orderNumber} />
            </div>
          )}
          <div className="view-order-shipping-billing-info-card">
            <div className="shipping-billing-summary-boxes">
              <SummaryBox
                summary="Shipping"
                address={order.shippingAddress}
                method={order.customLineItems[0]}
                isDesktop={isDesktop}
                shipMethodItemMap={shippingMethods}
              />
              <SummaryBox
                summary="Billing"
                address={order.billingAddress}
                method={order.paymentInfo.payments[0]}
                isDesktop={isDesktop}
              />
            </div>
            {isDesktop && (
              <div className="shipping-billing-print-tracking">
                <button
                  type="button"
                  className="print-link button button--default"
                  onClick={printPage}
                >
                  Print
                </button>
                <div>
                  <OrderTracker orderNum={orderNumber} />
                </div>
              </div>
            )}
          </div>
          <div className="view-order-container">
            <div className="shipment-and-warranty">
              <ShipmentInfo shipments={shipments} order={order} />
            </div>
            <div className="order-summaries">
              {warranties.length > 0 && (
                <WarrantyItems warranties={warranties} />
              )}
              <OrderSummary
                order={order}
                isDesktop={isDesktop}
                orderDate={orderDate}
                shippingTotal={order ? calculatetShippingTotal(order) : 0}
                convertCentsToDollars={convertCentsToDollars}
              />
              <WarrantyInfo />
              <ContactBox />
            </div>
          </div>
        </>
      ) : (
        <div className="view-order-loader">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default ViewOrder;
